import { useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CONTRACT_STATUS, formatDate, formatMoney, intervalLabel, visitsPerYear } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { ContractDialog } from "@/components/app/ContractDialog";

const ContractItem = ({ c, onEdit }) => {
  const [label, cls] = CONTRACT_STATUS[c.status];
  return (
    <li className="rounded-lg border border-slate-700/60 bg-slate-900/60 p-3" style={{ borderLeft: `3px solid ${c.color}` }} data-testid={`contract-item-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{c.service_name}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`contract-status-badge-${c.id}`}>{label}</span>
          </div>
          <div className="mt-0.5 text-xs text-slate-400">
            A cada {intervalLabel(c.effective_interval_value, c.effective_interval_unit)}{c.own_interval ? " (própria)" : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono font-semibold" data-testid={`contract-price-${c.id}`}>{c.price == null ? <span className="text-sm font-normal text-slate-500">Sem valor</span> : formatMoney(c.price)}</div>
          <div className="text-[11px] text-slate-500">por atendimento</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 font-mono text-xs text-slate-400">
        <span data-testid={`contract-dates-${c.id}`}>Último {formatDate(c.last_done)} · Próximo {formatDate(c.next_scheduled)} · {c.done_count} feito(s)</span>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onEdit} data-testid={`contract-edit-button-${c.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
      </div>
      {c.notes && <p className="mt-1 text-xs italic text-slate-400">{c.notes}</p>}
    </li>
  );
};

export const ClientServicesCard = ({ name, version }) => {
  const { bump } = useServices();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    api.get("/clients/services", { params: { name } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [name, version]);

  const yearly = items.filter((c) => c.status === "ativo" && c.price != null)
    .reduce((sum, c) => sum + c.price * visitsPerYear(c.effective_interval_value, c.effective_interval_unit), 0);
  const taken = items.map((c) => c.service_type);

  return (
    <div className="mt-4 rounded-xl border border-slate-700/60 bg-slate-800/60 p-4" data-testid="client-services-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Serviços contratados ({items.length})</div>
        <Button size="sm" variant="ghost" onClick={() => setDialog({ taken })} data-testid="contract-add-button"><Plus className="mr-1 h-3.5 w-3.5" /> Adicionar</Button>
      </div>
      {items.length === 0 ? <p className="py-3 text-center text-sm text-slate-500" data-testid="contracts-empty">Nenhum serviço contratado ainda.</p> : (
        <ul className="space-y-2" data-testid="contracts-list">
          {items.map((c) => <ContractItem key={c.id} c={c} onEdit={() => setDialog({ contract: c, taken })} />)}
        </ul>
      )}
      {yearly > 0 && (
        <div className="mt-3 flex items-center justify-between border-t border-slate-700/60 pt-3 text-sm" data-testid="contracts-yearly-total">
          <span className="text-slate-400">Previsão em 12 meses (serviços ativos)</span>
          <span className="font-mono font-semibold text-emerald-300">{formatMoney(yearly)}</span>
        </div>
      )}
      <ContractDialog state={dialog} clientName={name} onClose={() => setDialog(null)} onSaved={bump} />
    </div>
  );
};
